import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const DeleteUserModal = ({ isOpen, onClose, onConfirm, user, isPermanent = false }) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [confirmText, setConfirmText] = useState('');

  const handleConfirm = async () => {
    setIsDeleting(true);
    await onConfirm(user?.id, isPermanent);
    setIsDeleting(false);
    setConfirmText('');
    onClose();
  };

  const isConfirmValid = confirmText?.toLowerCase() === 'delete';

  if (!isOpen || !user) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 animate-fade-in">
      <div className="bg-card border border-border rounded-lg shadow-modal w-full max-w-md animate-slide-in">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-destructive/10">
              <Icon name="AlertTriangle" size={24} className="text-destructive" />
            </div>
            <h2 className="text-xl font-semibold text-foreground">
              {isPermanent ? 'Permanently Delete User' : 'Delete User'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-muted transition-colors duration-200"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
            <p className="text-sm text-foreground mb-2">
              You are about to {isPermanent ? 'permanently delete' : 'delete'} the following user:
            </p>
            <div className="flex items-center gap-3 mt-3">
              <div className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-semibold">
                {user?.initials || user?.fullName?.split(' ')?.map(n => n?.[0])?.join('')?.toUpperCase()}
              </div>
              <div>
                <p className="font-medium text-foreground">{user?.name || user?.fullName}</p>
                <p className="text-xs text-muted-foreground">{user?.email}</p>
              </div>
            </div>
          </div>

          {isPermanent ? (
            <div className="bg-warning/10 border border-warning/20 rounded-lg p-4">
              <div className="flex items-start gap-2">
                <Icon name="AlertCircle" size={18} className="text-warning mt-0.5" />
                <div className="text-sm text-foreground">
                  <p className="font-semibold mb-1">Warning: This action cannot be undone!</p>
                  <p>This will permanently delete:</p>
                  <ul className="list-disc list-inside mt-2 space-y-1 text-muted-foreground">
                    <li>User account and authentication</li>
                    <li>All role assignments</li>
                    <li>User profile data</li>
                  </ul>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-muted/50 border border-border rounded-lg p-4">
              <p className="text-sm text-foreground">
                This will deactivate the user account and revoke all role assignments. The user will no longer be able to access the system.
              </p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Type <span className="font-bold text-destructive">DELETE</span> to confirm
            </label>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e?.target?.value)}
              placeholder="Type DELETE"
              className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-destructive bg-background text-foreground"
            />
          </div>

          <div className="flex items-center gap-3 pt-4">
            <Button
              type="button"
              variant="destructive"
              fullWidth
              onClick={handleConfirm}
              disabled={!isConfirmValid || isDeleting}
              loading={isDeleting}
              iconName="Trash2"
              iconPosition="left"
              iconSize={18}
            >
              {isPermanent ? 'Permanently Delete' : 'Delete User'}
            </Button>
            <Button
              type="button"
              variant="outline"
              fullWidth
              onClick={onClose}
              disabled={isDeleting}
            >
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeleteUserModal;